// PageHeader.tsx - Reusable header component with permission-based actions

import React, { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './Pageheader.module.css';

export interface HeaderAction {
  key: string;
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  permission?: boolean; // If provided, button will be disabled when false
  className?: string;
}

export interface PageHeaderProps {
  title: string;
  subtitle?: string;
  onRefresh?: () => void;
  showColumnToggle?: boolean;
  columnToggleOpen?: boolean;
  onColumnToggleClick?: () => void;
  columnToggleContent?: React.ReactNode;
  actions?: HeaderAction[];
  showRefresh?: boolean;
  showCreateStudy?: boolean;
  showDefineFiles?: boolean;
  showUploadDocuments?: boolean;
  showDefinePreview?: boolean;
  showIssueManagement?: boolean;
  showProgressCharts?: boolean;
  permissions?: {
    canCreateStudy?: boolean;
    canViewDefineFiles?: boolean;
    canUploadDocuments?: boolean;
    canViewDefinePreview?: boolean;
    canAccessIssueManagement?: boolean;
    canViewProgressCharts?: boolean;
  };
  selectedStudyId?: number | null;
  onCreateStudy?: () => void;
  onDefineFiles?: () => void;
  onUploadDocuments?: () => void;
  onDefinePreview?: () => void;
  onIssueManagement?: () => void;
  onProgressCharts?: () => void;
}

const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  subtitle,
  onRefresh,
  showColumnToggle = false,
  columnToggleOpen = false,
  onColumnToggleClick,
  columnToggleContent,
  actions = [],
  showRefresh = false,
  showCreateStudy = false,
  showDefineFiles = false,
  showUploadDocuments = false,
  showDefinePreview = false,
  showIssueManagement = false,
  showProgressCharts = false,
  permissions = {},
  selectedStudyId,
  onCreateStudy,
  onDefineFiles,
  onUploadDocuments,
  onDefinePreview,
  onIssueManagement,
  onProgressCharts,
}) => {
  const navigate = useNavigate();
  const columnToggleRef = useRef<HTMLDivElement>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Close column toggle when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (columnToggleOpen && 
          columnToggleRef.current && 
          !columnToggleRef.current.contains(event.target as Node)) {
        onColumnToggleClick?.();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [columnToggleOpen, onColumnToggleClick]);

  // Default permissions to true if not specified
  const {
    canCreateStudy = true,
    canViewDefineFiles = true,
    canUploadDocuments = true,
    canViewDefinePreview = true,
    canAccessIssueManagement = true,
    canViewProgressCharts = true,
  } = permissions;

  // Check if study-specific actions should be disabled
  const isStudyActionDisabled = (requiresStudy: boolean) => {
    return requiresStudy && !selectedStudyId;
  };

  const handleRefresh = async () => {
    if (onRefresh && !isRefreshing) {
      setIsRefreshing(true);
      try {
        await onRefresh();
      } finally {
        // Stop spinning after a short delay to show the animation
        setTimeout(() => setIsRefreshing(false), 500);
      }
    }
  };

  return (
    <div className={styles.header}>
      <div className={styles.headerLeft}>
        <div>
          <h1 className={styles.title}>{title}</h1>
          {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
        </div>
      </div>

      <div className={styles.headerActions}>
        {/* Utility Buttons - Always visible */}
        {showRefresh && onRefresh && (
          <button
            className={`${styles.iconButton} ${isRefreshing ? styles.spinning : ''}`}
            onClick={handleRefresh}
            title="Refresh"
            disabled={isRefreshing}
          >
            <svg 
              width="20" 
              height="20" 
              viewBox="0 0 24 24" 
              fill="none"
            >
              <path
                d="M1 4v6h6M23 20v-6h-6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
              <path
                d="M20.49 9A9 9 0 005.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 013.51 15"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        )}

        {showColumnToggle && onColumnToggleClick && (
          <div className={styles.columnToggleWrapper} ref={columnToggleRef}>
            <button
              className={`${styles.iconButton} ${columnToggleOpen ? styles.active : ''}`}
              onClick={onColumnToggleClick}
              title="Toggle Columns"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path
                  d="M3 6h18M3 12h18M3 18h18"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </button>
            {columnToggleOpen && columnToggleContent}
          </div>
        )}

        {/* Divider between utility and permission-based buttons */}
        {(showCreateStudy || showDefineFiles || showUploadDocuments || showDefinePreview || showIssueManagement || showProgressCharts) && (
          <div className={styles.divider} />
        )}

        {/* Permission-based Action Buttons */}
        
        {/* Create Study Button */}
        {showCreateStudy && (
          <button
            className={`${styles.actionButton} ${!canCreateStudy ? styles.disabled : ''}`}
            onClick={onCreateStudy}
            disabled={!canCreateStudy}
            title={canCreateStudy ? 'Create New Study' : 'No permission to create study'}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path
                d="M12 5v14M5 12h14"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
              <circle
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="2"
              />
            </svg>
            <span className={styles.buttonLabel}>New Study</span>
          </button>
        )}

        {/* Define Files Button */}
        {showDefineFiles && (
          <button
            className={`${styles.actionButton} ${!canViewDefineFiles ? styles.disabled : ''}`}
            onClick={onDefineFiles}
            disabled={!canViewDefineFiles}
            title={canViewDefineFiles ? 'View Define Files' : 'No permission to view define files'}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path
                d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
              <path
                d="M14 2v6h6M16 13H8M16 17H8M10 9H8"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
            <span className={styles.buttonLabel}>Define Files</span>
          </button>
        )}

        {/* Upload Documents Button */}
        {showUploadDocuments && (
          <button
            className={`${styles.actionButton} ${
              !canUploadDocuments || isStudyActionDisabled(true) ? styles.disabled : ''
            }`}
            onClick={onUploadDocuments}
            disabled={!canUploadDocuments || isStudyActionDisabled(true)}
            title={
              !canUploadDocuments
                ? 'No permission to upload documents'
                : isStudyActionDisabled(true)
                ? 'Select a study to upload documents'
                : 'Upload Documents'
            }
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path
                d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
            <span className={styles.buttonLabel}>Upload</span>
          </button>
        )}

        {/* Define Preview Button */}
        {showDefinePreview && (
          <button
            className={`${styles.actionButton} ${
              !canViewDefinePreview || isStudyActionDisabled(true) ? styles.disabled : ''
            }`}
            onClick={onDefinePreview}
            disabled={!canViewDefinePreview || isStudyActionDisabled(true)}
            title={
              !canViewDefinePreview
                ? 'No permission to view define preview'
                : isStudyActionDisabled(true)
                ? 'Select a study to view preview'
                : 'View Define Preview'
            }
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path
                d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
              <circle
                cx="12"
                cy="12"
                r="3"
                stroke="currentColor"
                strokeWidth="2"
              />
            </svg>
            <span className={styles.buttonLabel}>Preview</span>
          </button>
        )}

        {/* Issue Management Button */}
        {showIssueManagement && (
          <button
            className={`${styles.actionButton} ${
              !canAccessIssueManagement || isStudyActionDisabled(true) ? styles.disabled : ''
            }`}
            onClick={onIssueManagement}
            disabled={!canAccessIssueManagement || isStudyActionDisabled(true)}
            title={
              !canAccessIssueManagement
                ? 'No permission to access issue management'
                : isStudyActionDisabled(true)
                ? 'Select a study to view issues'
                : 'Issue Management'
            }
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <circle
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="2"
              />
              <path
                d="M12 8v4M12 16h.01"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
            <span className={styles.buttonLabel}>Issues</span>
          </button>
        )}

        {/* Progress Charts Button */}
        {showProgressCharts && (
          <button
            className={`${styles.actionButton} ${
              !canViewProgressCharts || isStudyActionDisabled(true) ? styles.disabled : ''
            }`}
            onClick={onProgressCharts}
            disabled={!canViewProgressCharts || isStudyActionDisabled(true)}
            title={
              !canViewProgressCharts
                ? 'No permission to view progress charts'
                : isStudyActionDisabled(true)
                ? 'Select a study to view charts'
                : 'View Progress Charts'
            }
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path
                d="M3 3v18h18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
              <path
                d="M18 17V9M13 17v-4M8 17v-2"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
            <span className={styles.buttonLabel}>Charts</span>
          </button>
        )}

        {/* Custom Actions */}
        {actions.map((action) => (
          <button
            key={action.key}
            className={`${styles.actionButton} ${
              action.permission === false ? styles.disabled : ''
            } ${action.className || ''}`}
            onClick={action.onClick}
            disabled={action.permission === false}
            title={action.label}
          >
            {action.icon}
            {action.label && <span className={styles.buttonLabel}>{action.label}</span>}
          </button>
        ))}
      </div>
    </div>
  );
};

export default PageHeader;